import React from 'react';
import PropTypes from 'prop-types';
import {
  allowedTableDataShape,
  allowedCellContentTypes,
  allowedLanguages,
  allowedRenderLocations,
  allowedFilterDataPropTypes,
  allowedColumnTextAligns,
  allowedCellVerticalAligns,
  allowedTranslationLabelProps
} from '../utils/allowedPropTypes';
import DataTableContent from './_DataTableContent';

const addColumnOnce = function (columnCollection, columnToAdd) {
  const arrayLen = columnCollection.length;
  const {
    columnId: newColumnId,
    valueType: newValueType
  } = columnToAdd;

  for (let i = 0; i < arrayLen; i += 1) {
    const {
      columnId: existingColumnId,
      valueType: existingValueType
    } = columnCollection[i];

    if (existingColumnId === newColumnId) {
      if (existingValueType !== newValueType) {
        throw new Error(`
          Error when getting data for table column headers:
          Value types for the same object key names have to be constant!
          Both "'${existingValueType}" and "${newValueType}"
          value types were found for table column "${existingColumnId}"
        `);
      }

      return;
    }
  }

  columnCollection.push(columnToAdd);
};

class DataTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeDataLabel: props.tableData.dataLabel, // TODO: Get rid off this premature and overcomplicated optimization
      activePageNumber: (props.paginationData) ? props.paginationData.pageNumber : 1,
      preparedTableData: []
    };

    this.processSubComponentData = this.processSubComponentData.bind(this);
    this.getTableAttributes = this.getTableAttributes.bind(this);
    this.getStoredTableData = this.getStoredTableData.bind(this);
    this.refTable = React.createRef();
  }

  componentDidMount() {
    this.updateState();
  }

  componentDidUpdate() {
    this.updateState();
  }

  // TODO: Get rid off this premature and overcomplicated optimization:
  getStoredTableData(dataLabel, paginationData) {
    const { preparedTableData } = this.state;

    return preparedTableData.find(storedTableData => (
      storedTableData.dataLabel === dataLabel && (
        (paginationData !== undefined && storedTableData.pageNumber === paginationData.pageNumber)
        || (paginationData === undefined && storedTableData.pageNumber === 1)
      )
    ));
  }

  getTableAttributes() {
    const {
      props,
      refTable,
      getStoredTableData
    } = this;

    const {
      classNames: passedClassNames = [],
      cellVerticalAlign = 'middle',
      hasDataLoaded = true,
      languageCode = 'en',
      showFancyRows = false,
      tableData,
      labels
    } = props;

    let { paginationData } = props;

    const classNames = [...passedClassNames];

    // TODO: Get rid off this premature and overcomplicated optimization:
    const receivedDataLabel = tableData.dataLabel;

    // TODO: Get rid off this premature and overcomplicated optimization:
    const storedTableData = getStoredTableData(receivedDataLabel, paginationData);

    const isDataReady = (hasDataLoaded && storedTableData !== undefined);
    let rawTableData = [];
    let processedTableData = {};
    let processedColumns = [];
    let processedItems = [];
    const tableAttributes = {};

    classNames.push('data-lists', 'component-data-table', `cell-content-align-${cellVerticalAlign}`);

    if (showFancyRows) {
      classNames.push('with-fancy-rows');
    }

    if (isDataReady) { // TODO: check for error! (e.g. backend error => isError)
      rawTableData = storedTableData.rawData;
      processedTableData = storedTableData.processedData;
      processedColumns = processedTableData.tableColumns;
      processedItems = processedTableData.tableItems;
    }
    else {
      classNames.push('table-loading');
    }

    if (processedItems.length < 1) {
      classNames.push('data-table-no-results');
    }

    tableAttributes.className = classNames.join(' ');

    const attributesOfTableParts = {
      head: {},
      body: {
        tableItems: processedItems,
        dataLabel: receivedDataLabel, // TODO: Get rid off this premature and overcomplicated optimization
        labels
      },
      foot: {}
    };

    Object.keys(attributesOfTableParts).forEach(tablePartName => {
      const isTableFoot = tablePartName === 'foot';
      const tablePart = attributesOfTableParts[tablePartName];

      if (!isTableFoot) {
        tablePart.tableColumns = processedColumns;
      }

      if (paginationData) {
        paginationData = {
          ...paginationData,
          languageCode,
          isDataReady,
          columnCount: processedColumns.length,
          rowCount: rawTableData.length
        };

        if (isTableFoot) {
          paginationData.refParentTable = refTable;
        }

        tablePart.paginationData = paginationData;
      }

      if (languageCode) {
        tablePart.languageCode = languageCode;
      }
    });

    return {
      isDataReady,
      tableData,
      tableAttributes,
      attributesOfTableParts
    };
  }

  processSubComponentData(receivedDataToProcess) {
    const {
      props: {
        columnSettings,
        tableId,
        tableData
      }
    } = this;

    const exportData = {
      tableColumns: [],
      tableItems: []
    };

    const { preprocessData } = tableData;
    const dataToProcess = (preprocessData)
      ? preprocessData(receivedDataToProcess)
      : receivedDataToProcess;

    const processNestedData = function (passedDataArray, inputDataObject, inputNestingData = {
      parentNestingPath: tableId,
      nestingGroupCellIndex: 0,
      nestingLevelRowSpan: 0,
      nestingLevelCells: []
    }) {
      let outputDataObject = Object.assign({}, inputDataObject);
      const outputNestingData = Object.assign({}, inputNestingData);

      const {
        tableColumns: columnsToExport,
        tableItemCells: cellsToExport
      } = outputDataObject;

      const { parentNestingPath } = outputNestingData;
      let { firstRowIndex } = outputNestingData;
      let sumOfNestedRowSpans = 0;

      passedDataArray.forEach(dataObject => {
        const nestingLevelCells = [];
        const objectKeys = Object.keys(dataObject);
        let nestingLevelRowSpan = 1;

        objectKeys.forEach((objectKey, index) => {
          let propertyValue = dataObject[objectKey];
          let cellData = {};
          const nestingPath = `${parentNestingPath}-${objectKey}`;
          let appliedColumnSettings;
          let isSkipped = false;

          if (columnSettings !== undefined) {
            appliedColumnSettings = columnSettings.find(({ nestingPath: predefinedPath }) => {
              if (predefinedPath === undefined) {
                throw new Error(
                  'ERROR: nestingPath must be defined in the column setting object '
                  + 'in order to be able to identify the column by its ID. '
                  + '(Make sure it matches the column ID pattern - a string of the nesting '
                  + 'path separated and started by "/")'
                );
              }

              return (`${tableId}-${predefinedPath}`) === nestingPath;
            });
          }

          const hasColumnSettings = (appliedColumnSettings !== undefined);
          if (hasColumnSettings && appliedColumnSettings.isSkipped !== undefined) {
            ({ isSkipped } = appliedColumnSettings);
          }

          if (!isSkipped) {
            let hasNestedArray = Array.isArray(propertyValue);
            if (propertyValue) {
              if (typeof (propertyValue) === 'object') {
                if (!hasNestedArray && propertyValue.$$typeof === undefined) {
                  propertyValue = [propertyValue];
                  hasNestedArray = true;
                }
              }

              else {
                propertyValue = propertyValue.toString();
              }
            }

            if (hasNestedArray) {
              let childNestingData = {
                firstRowIndex,
                nestingGroupCellIndex: firstRowIndex,
                nestingLevelRowSpan: 1,
                parentNestingPath: nestingPath,
                nestingLevelCells: []
              };

              const processedChildData = processNestedData(
                propertyValue,
                outputDataObject,
                childNestingData
              );

              ({ outputDataObject, outputNestingData: childNestingData } = processedChildData);

              if (nestingLevelRowSpan < childNestingData.nestingLevelRowSpan) {
                ({ nestingLevelRowSpan } = childNestingData);
              }

              cellData = {
                cellContent: '(empty group)', // if the nested array or JSON is empty // TODO: // TRANSLATE
                spannedRows: childNestingData.nestingLevelRowSpan,
                nestedCells: childNestingData.nestingLevelCells
              };
            }

            const hasColumnHeadingContent = (
              hasColumnSettings
              && appliedColumnSettings.columnHeadingContent !== undefined
            );

            const columnHeadingContent = (hasColumnHeadingContent)
              ? appliedColumnSettings.columnHeadingContent
              : objectKey;

            const columnData = {
              columnHeadingContent,
              columnId: nestingPath,
              columnTitle: objectKey,
              isGroupColumn: hasNestedArray,
              valueType: typeof (propertyValue),

              // TODO: could be deconstructed from appliedCappliedColumnSettings:
              noCellPadding: hasColumnSettings && appliedColumnSettings.noCellPadding,

              // TODO: could be deconstructed from appliedCappliedColumnSettings:
              isMobileTermHidden: hasColumnSettings && appliedColumnSettings.isMobileTermHidden
            };

            // Arrange text in the last cells to the right by default:
            if (index > 0 && index + 1 > objectKeys.length - 1) {
              columnData.columnTextAlign = 'right';
            }

            if (hasColumnSettings) {
              if (appliedColumnSettings.columnTextAlign !== undefined) {
                columnData.columnTextAlign = appliedColumnSettings.columnTextAlign;
              }

              if (appliedColumnSettings.dataFilter !== undefined) {
                columnData.dataFilter = appliedColumnSettings.dataFilter;
              }

              if (appliedColumnSettings.classNames !== undefined) {
                columnData.classNames = appliedColumnSettings.classNames;
              }
            }

            addColumnOnce(columnsToExport, columnData);

            nestingLevelCells.push({
              ...cellData,
              firstRowIndex,
              columnHeadingContent,
              columnId: nestingPath,
              cellContent: propertyValue,
              isNestedCell: (parentNestingPath !== tableId)
            });
          }
        });

        nestingLevelCells.map(iteratedNestingLevelCell => {
          const nestingLevelCell = Object.assign({}, iteratedNestingLevelCell);

          if (nestingLevelCell.spannedRows === undefined) {
            nestingLevelCell.spannedRows = nestingLevelRowSpan;
          }

          return nestingLevelCell;
        });

        outputNestingData.nestingLevelCells.push(nestingLevelCells);

        cellsToExport.push(...nestingLevelCells);
        sumOfNestedRowSpans += nestingLevelRowSpan;
        firstRowIndex += nestingLevelRowSpan;
      });

      outputNestingData.nestingLevelRowSpan = sumOfNestedRowSpans;

      return {
        outputDataObject,
        outputNestingData
      };
    };

    dataToProcess.forEach(tableItemData => {
      const tableItems = [tableItemData];
      const processingResult = processNestedData(
        tableItems,
        {
          tableColumns: exportData.tableColumns,
          tableItemCells: []
        },
        {
          firstRowIndex: 0,
          parentNestingPath: tableId,
          nestingLevelRowSpan: 0,
          nestingLevelCells: []
        }
      );

      const {
        outputDataObject,
        outputNestingData
      } = processingResult;

      exportData.tableColumns = outputDataObject.tableColumns;
      exportData.tableItems.push({
        itemRowsLen: outputNestingData.nestingLevelRowSpan,
        tableItemCells: outputDataObject.tableItemCells
      });
    });

    return exportData;
  }

  updateState() {
    const {
      props: {
        tableData: {
          dataLabel, // TODO: Get rid off this premature and overcomplicated optimization
          dataToProcess
        },
        paginationData,
        hasDataLoaded = true
      },
      state,
      getStoredTableData,
      processSubComponentData
    } = this;

    // TODO: Get rid off this premature and overcomplicated optimization:
    const storedTableData = getStoredTableData(dataLabel, paginationData);

    const pageNumber = (paginationData) ? paginationData.pageNumber : 1;
    const stateCopy = Object.assign({}, state);
    let isStateNew = false;

    // TODO: Get rid off this premature and overcomplicated optimization:
    if (stateCopy.activeDataLabel !== dataLabel) {
      stateCopy.activeDataLabel = dataLabel;
      isStateNew = true;
    }

    if (stateCopy.activePageNumber !== pageNumber) {
      stateCopy.activePageNumber = pageNumber;
      isStateNew = true;
    }

    if (hasDataLoaded && storedTableData === undefined) {
      stateCopy.preparedTableData.push({
        pageNumber,
        dataLabel, // TODO: Get rid off this premature and overcomplicated optimization
        rawData: dataToProcess,
        processedData: processSubComponentData(dataToProcess)
      });
      isStateNew = true;
    }

    if (isStateNew) {
      this.setState(stateCopy);
    }
  }

  render() {
    const {
      refTable,
      getTableAttributes,
      props: {
        tableId,
        hideTableHead
      }
    } = this;

    const {
      tableData,
      isDataReady,
      tableAttributes,
      attributesOfTableParts
    } = getTableAttributes();

    const contentProps = {
      tableData,
      attributesOfTableParts,
      isDataReady,
      hideTableHead
    };

    return (
      <article className='data-table-wrap'>
        <table id={tableId} ref={refTable} {...tableAttributes}>
          <DataTableContent {...contentProps} />
        </table>
      </article>
    );
  }
}

DataTable.propTypes = {
  tableId: PropTypes.string.isRequired,
  tableData: PropTypes.shape(allowedTableDataShape).isRequired,
  classNames: PropTypes.arrayOf(PropTypes.string),
  cellVerticalAlign: PropTypes.oneOf(allowedCellVerticalAligns),
  hasDataLoaded: PropTypes.bool,
  hideTableHead: PropTypes.bool,
  languageCode: PropTypes.oneOf(allowedLanguages),
  labels: PropTypes.shape(allowedTranslationLabelProps),
  showFancyRows: PropTypes.bool,
  paginationData: PropTypes.shape({
    renderLocation: PropTypes.oneOf(allowedRenderLocations).isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    pageNumber: PropTypes.number.isRequired,
    goToPageNumber: PropTypes.func.isRequired
  }),
  columnSettings: PropTypes.arrayOf(PropTypes.shape({
    nestingPath: PropTypes.string.isRequired,
    isSkipped: PropTypes.bool,
    columnHeadingContent: PropTypes.oneOfType(allowedCellContentTypes),
    columnTextAlign: PropTypes.oneOf(allowedColumnTextAligns),
    classNames: PropTypes.arrayOf(PropTypes.string),
    dataFilter: PropTypes.shape(allowedFilterDataPropTypes),
    noCellPadding: PropTypes.bool,
    isMobileTermHidden: PropTypes.bool
  }))
};

export default DataTable;